import { useState } from "react";
import axios from "axios";
import { PRICING_SHEET } from "@/data/site";
import { Reveal, MaskLineInView } from "@/components/site/Reveal";
import { Download, FileText, Check } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field =
  "w-full bg-background border border-border px-4 py-3 text-base outline-none focus:border-foreground focus:ring-1 focus:ring-ring transition-colors duration-200 placeholder:text-muted-foreground rounded-none";
const label = "text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2 block";

export const Services = () => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "" });
  const [loading, setLoading] = useState(false);
  const [unlocked, setUnlocked] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/pricing-requests`, form);
      setUnlocked(true);
      toast.success("Pricing sheet unlocked!");
    } catch {
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  const openDialog = () => {
    setOpen(true);
  };

  return (
    <section id="services" className="bg-background py-24 md:py-32 scroll-mt-16" data-testid="services">
      <div className="mx-auto max-w-[1400px] px-6 md:px-8">
        <Reveal>
          <span className="text-xs tracking-[0.35em] uppercase text-muted-foreground">Pricing Sheet</span>
        </Reveal>
        <h2 className="font-display uppercase tracking-tighter leading-[0.9] text-4xl sm:text-5xl md:text-6xl mt-5">
          <MaskLineInView>Services</MaskLineInView>
          <MaskLineInView delay={0.08} className="text-muted-foreground">& Pricing</MaskLineInView>
        </h2>

        <div id="pricing" className="scroll-mt-24 mt-16 grid grid-cols-1 lg:grid-cols-12 gap-px bg-border border border-border">
          <div className="lg:col-span-12 bg-background p-8 md:p-14">
            <p className="text-base md:text-lg text-muted-foreground max-w-xl">
              From dealership deals and fleet rates to our Basic Heavy Duty Detail package (6 wheels + 1 fuel tank),
              full aluminum polishing across tanks, covers, and battery boxes, plus interior and exterior detailing
              options — every truck is priced to the work it needs. Request the full sheet to see all our packages and
              bundle deals.
            </p>
            <ul className="mt-8 space-y-3 text-sm md:text-base">
              {[
                "Dealership & fleet rates",
                "Bundles & package deals",
                "Aluminum polishing packages",
              ].map((t) => (
                <li key={t} className="flex items-center gap-3 text-muted-foreground">
                  <Check size={16} className="text-foreground shrink-0" /> {t}
                </li>
              ))}
            </ul>
            <Reveal delay={0.1}>
              <button
                data-testid="request-pricing-cta"
                onClick={openDialog}
                className="mt-10 inline-flex items-center gap-3 bg-foreground text-background px-8 py-4 text-sm tracking-[0.2em] uppercase border border-foreground hover:bg-transparent hover:text-foreground transition-colors duration-300"
              >
                <FileText size={16} /> Request Pricing Sheet
              </button>
            </Reveal>
          </div>
        </div>
      </div>

      <Dialog
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) setUnlocked(false);
        }}
      >
        <DialogContent
          data-testid="pricing-dialog"
          className="bg-popover border border-border rounded-none max-w-lg text-foreground"
        >
          {!unlocked ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-display uppercase tracking-tight text-2xl">
                  Request Pricing Sheet
                </DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  Tell us where to send it — you'll get instant access on the next screen.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="mt-2 space-y-4" data-testid="pricing-form">
                <div>
                  <label className={label}>Name *</label>
                  <input data-testid="pricing-name" className={field} value={form.name} onChange={set("name")} placeholder="Your name" />
                </div>
                <div>
                  <label className={label}>Email *</label>
                  <input data-testid="pricing-email" type="email" className={field} value={form.email} onChange={set("email")} placeholder="you@email.com" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={label}>Phone</label>
                    <input data-testid="pricing-phone" className={field} value={form.phone} onChange={set("phone")} placeholder="Optional" />
                  </div>
                  <div>
                    <label className={label}>Company</label>
                    <input data-testid="pricing-company" className={field} value={form.company} onChange={set("company")} placeholder="Optional" />
                  </div>
                </div>
                <button
                  data-testid="pricing-submit"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-foreground text-background px-8 py-4 text-sm tracking-[0.2em] uppercase border border-foreground hover:bg-transparent hover:text-foreground transition-colors duration-300 disabled:opacity-50"
                >
                  {loading ? "Unlocking…" : "Get The Pricing Sheet"}
                </button>
              </form>
            </>
          ) : (
            <div data-testid="pricing-unlocked">
              <DialogHeader>
                <DialogTitle className="font-display uppercase tracking-tight text-2xl flex items-center gap-2">
                  <Check size={22} /> Here's Your Sheet
                </DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  Thanks {form.name.split(" ")[0] || "there"} — view it below or download to keep & share.
                </DialogDescription>
              </DialogHeader>
              <div className="mt-3 border border-border max-h-[55vh] overflow-y-auto">
                <img src={PRICING_SHEET} alt="Heavy Duty Detailing pricing sheet" className="w-full h-auto block" />
              </div>
              <a
                data-testid="pricing-download"
                href={PRICING_SHEET}
                download="Heavy-Duty-Detailing-Pricing.png"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 w-full inline-flex items-center justify-center gap-3 bg-foreground text-background px-8 py-4 text-sm tracking-[0.2em] uppercase border border-foreground hover:bg-transparent hover:text-foreground transition-colors duration-300"
              >
                <Download size={16} /> Download Pricing Sheet
              </a>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
